/**
 * Model selector — multi-provider dropdown showing all connected providers
 * and their models. Each provider group is a labeled section; clicking a model
 * switches the chat to that provider + model in one action.
 *
 * Provider-agnostic: works with any configured provider (Anthropic, Groq,
 * Gemini, OpenAI, OpenRouter, Ollama, etc.) — no hardcoded provider logic.
 */
import { useQuery } from '@tanstack/react-query';
import { isDesktopBackend } from '@/lib/surface';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { chatStore, useChatStore } from '@/lib/chatStore';
import { classifyRouteReason } from '@/lib/routeReason';
import { api } from '@/lib/api';
import { STALE_SSE_COVERED_MS } from '@/lib/uiConstants';
import { cn } from '@onestone/ui/utils';

interface CatalogModel {
  id: string;
  name: string;
  provider: string;
  /** One-line explainer shown under the name (family aliases carry this). */
  description?: string;
  contextWindow: number;
  /** Transports this entry is offered on. Undefined = all (pinned snapshots). */
  transports?: ReadonlyArray<'api' | 'subscription'>;
  extendedContext?: {
    window: number;
    transports: ReadonlyArray<'api' | 'subscription'>;
  };
}

interface ClaudeStatus {
  authMode: 'api-key' | 'subscription';
  cliInstalled: boolean;
  credentialsLinked: boolean;
  subscriptionConfigured: boolean;
}

interface ChatProviderEntry {
  id: string;
  name: string;
  models: Array<{ id: string; name: string }>;
}

function fmtContext(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  return `${Math.round(n / 1000)}K`;
}

interface ContextTierPillProps {
  baseWindow: number;
  extendedWindow: number;
  activeTier: 'base' | 'extended' | undefined;
  onSelect: (useExtended: boolean) => void;
}

function ContextTierPill({ baseWindow, extendedWindow, activeTier, onSelect }: ContextTierPillProps) {
  return (
    <span className="inline-flex shrink-0 items-center overflow-hidden rounded-md border border-border/60 text-xs tabular-nums">
      <button
        type="button"
        data-testid="tier-pill-base"
        aria-pressed={activeTier === 'base'}
        className={cn(
          'min-h-9 px-2 transition-colors',
          activeTier === 'base' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted',
        )}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(false);
        }}
      >
        {fmtContext(baseWindow)}
      </button>
      <span aria-hidden="true" className="h-5 w-px bg-border/60" />
      <button
        type="button"
        data-testid="tier-pill-extended"
        aria-pressed={activeTier === 'extended'}
        className={cn(
          'min-h-9 px-2 transition-colors',
          activeTier === 'extended' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted',
        )}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(true);
        }}
      >
        {fmtContext(extendedWindow)}
      </button>
    </span>
  );
}

/** Display labels for provider IDs. Falls back to the raw ID. */
const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  groq: 'Groq',
  mistral: 'Mistral',
  gemini: 'Google Gemini',
  ollama: 'Ollama',
  openrouter: 'OpenRouter',
  openai: 'OpenAI',
};

/** Short labels for the compact trigger button. */
const PROVIDER_SHORT: Record<string, string> = {
  subscription: 'Sub',
  anthropic: 'API',
  groq: 'Groq',
  mistral: 'Mistral',
  gemini: 'Gemini',
  ollama: 'Ollama',
  openrouter: 'OR',
  openai: 'OpenAI',
};

/** Dot color per provider for visual identification. */
const PROVIDER_DOT: Record<string, string> = {
  subscription: 'bg-emerald-500',
  anthropic: 'bg-amber-500',
  groq: 'bg-orange-500',
  mistral: 'bg-blue-500',
  gemini: 'bg-sky-500',
  ollama: 'bg-violet-500',
  openrouter: 'bg-pink-500',
  openai: 'bg-green-500',
};

/** Determine the transport for extended-context checks. */
function transportForProvider(providerId: string): 'subscription' | 'api' {
  return providerId === 'subscription' ? 'subscription' : 'api';
}

/** Whether a catalog entry is offered on a transport. Undefined `transports` = all. */
function offeredOn(m: CatalogModel, transport: 'subscription' | 'api'): boolean {
  return m.transports?.includes(transport) ?? true;
}

export function ModelSelector() {
  const {
    provider: currentProvider,
    model: currentModel,
    useExtendedContext: currentExtended,
    routeReason,
    routeEffort,
  } = useChatStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const catalog = useQuery<{
    models: CatalogModel[];
    default: string;
    apiProviders: string[];
  }>({
    queryKey: ['model-catalog'],
    queryFn: () => api('/api/models'),
    staleTime: STALE_SSE_COVERED_MS,
  });

  const claudeStatus = useQuery<ClaudeStatus>({
    queryKey: ['claude-status'],
    queryFn: () => api('/api/claude/status'),
    staleTime: STALE_SSE_COVERED_MS,
    // Desktop-server endpoint (CLI subscription) — never poll it on the cloud
    // bundle. Backend-surface gated, not isElectron(): the relay PWA talks
    // to the desktop server and must see subscription models in the picker.
    enabled: isDesktopBackend(),
  });

  const chatProviders = useQuery<ChatProviderEntry[]>({
    queryKey: ['chat', 'providers'],
    queryFn: () => api('/api/chat/providers'),
    staleTime: STALE_SSE_COVERED_MS,
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const allModels = catalog.data?.models ?? [];
  // API-transport providers (real API key / local endpoint), EXCLUDING the
  // Claude subscription. Drives the per-provider API groups so a subscription-
  // only user gets no phantom "Anthropic API" group.
  const apiProviders = catalog.data?.apiProviders ?? [];
  // Subscription group is visible when the CLI is installed AND has credentials —
  // regardless of the authMode toggle. The toggle controls default routing
  // preference, not whether the "Subscription (CLI)" group appears.
  const subscriptionAvailable =
    claudeStatus.data?.cliInstalled &&
    (claudeStatus.data?.credentialsLinked || claudeStatus.data?.subscriptionConfigured);
  const chatProviderList = chatProviders.data ?? [];

  // Build provider groups — each connected provider with its models.
  // Subscription (CLI) and Anthropic API are separate groups when both are
  // available, since they share the same models but run through different
  // transports (each with different context-window capabilities).
  const groups: Array<{
    id: string;
    label: string;
    models: CatalogModel[];
  }> = [];

  // Subscription group: every Anthropic model, via subscription OAuth. Catalog
  // order is newest-first per family, so the current release of each sits at the
  // top. Only explicit version ids are listed — family aliases are not offered,
  // because the CLI resolves them by CLI version rather than by what is newest.
  if (subscriptionAvailable) {
    groups.push({
      id: 'subscription',
      label: 'Subscription',
      models: allModels.filter((m) => m.provider === 'anthropic' && offeredOn(m, 'subscription')),
    });
  }

  // All other configured providers — from catalog models + live provider lists
  for (const providerId of apiProviders) {
    if (providerId === 'agentmail') continue; // Not a model provider

    // For Anthropic with API key, show as a separate group from subscription
    const label = providerId === 'anthropic' ? 'Anthropic API' : (PROVIDER_LABELS[providerId] ?? providerId);

    // Merge: catalog models for this provider + any live-listed models not in
    // catalog. Transport-filtered so subscription-only aliases never leak into
    // an API-key provider group (the raw Messages API rejects bare aliases).
    const catalogModels = allModels.filter((m) => m.provider === providerId && offeredOn(m, 'api'));
    const liveEntry = chatProviderList.find((p) => p.id === providerId);
    const liveOnlyModels: CatalogModel[] = [];
    if (liveEntry) {
      for (const lm of liveEntry.models) {
        if (!catalogModels.some((cm) => cm.id === lm.id)) {
          liveOnlyModels.push({
            id: lm.id,
            name: lm.name,
            provider: providerId,
            contextWindow: 0,
          });
        }
      }
    }

    const combined = [...catalogModels, ...liveOnlyModels];
    if (combined.length > 0) {
      groups.push({ id: providerId, label, models: combined });
    }
  }

  const currentModelSpec = allModels.find((m) => m.id === currentModel);
  const currentModelName = currentModelSpec?.name ?? currentModel;
  const currentTransport = transportForProvider(currentProvider);
  const currentWindow =
    currentExtended && currentModelSpec?.extendedContext?.transports.includes(currentTransport)
      ? currentModelSpec.extendedContext.window
      : (currentModelSpec?.contextWindow ?? 0);

  const providerLabel = PROVIDER_SHORT[currentProvider] ?? currentProvider;
  const dotColor = PROVIDER_DOT[currentProvider] ?? 'bg-gray-500';

  const routeNotice = classifyRouteReason(routeReason);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="model-selector-trigger"
        className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs transition-colors hover:bg-muted"
        onClick={() => setOpen(!open)}
        title={routeNotice ? routeReason : undefined}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
        <span className="font-medium">{providerLabel}</span>
        <span className="text-muted-foreground">·</span>
        <span className="max-w-[140px] truncate">{currentModelName}</span>
        {currentWindow > 0 && <span className="text-muted-foreground tabular-nums">{fmtContext(currentWindow)}</span>}
        {routeEffort && (
          // The effort the last turn's model was sent (route event); none shown = its default.
          <span className="text-muted-foreground" aria-label={`Effort: ${routeEffort}`}>
            {routeEffort}
          </span>
        )}
        {routeNotice && (
          <span
            className="flex items-center gap-1 rounded-sm bg-amber-500/15 px-1 py-0.5 text-2xs font-medium uppercase leading-none tracking-wide text-amber-600 dark:text-amber-400"
            aria-label={`Route notice: ${routeReason}`}
          >
            <AlertTriangle className="h-2.5 w-2.5" />
            {routeNotice.label}
          </span>
        )}
        <ChevronDown className="h-3 w-3 text-muted-foreground" />
      </button>

      {open && (
        <div
          data-testid="model-selector-panel"
          data-active-connection={currentProvider ?? 'none'}
          className="absolute left-0 top-full z-50 mt-1 max-h-[min(70vh,500px)] w-80 overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-lg"
        >
          {groups.length === 0 ? (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground">
              No AI providers connected. Go to <strong>Connections</strong> to add an API key or sign in.
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.id} data-testid={`provider-group-${group.id}`} className="mb-1">
                <div className="flex items-center gap-1.5 px-2 py-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${PROVIDER_DOT[group.id] ?? 'bg-gray-500'}`} />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </span>
                </div>
                {group.models.map((m) => {
                  const providerForRow = group.id;
                  const isActiveRow = currentProvider === providerForRow && currentModel === m.id;
                  const transport = transportForProvider(group.id);
                  const extOnTransport = m.extendedContext?.transports.includes(transport);

                  if (extOnTransport && m.extendedContext) {
                    const activeTier = isActiveRow ? (currentExtended ? 'extended' : 'base') : undefined;
                    return (
                      <div
                        key={`${group.id}-${m.id}`}
                        data-testid={`model-row-${m.id}`}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs',
                          isActiveRow && 'bg-primary/10 text-primary',
                        )}
                      >
                        <span className="flex min-w-0 flex-col">
                          <span className="font-medium">{m.name}</span>
                          {m.description && (
                            <span className="text-2xs leading-snug text-muted-foreground">{m.description}</span>
                          )}
                        </span>
                        <ContextTierPill
                          baseWindow={m.contextWindow}
                          extendedWindow={m.extendedContext.window}
                          activeTier={activeTier}
                          onSelect={(useExt) => {
                            chatStore.setProvider(providerForRow, m.id, useExt);
                            setOpen(false);
                          }}
                        />
                      </div>
                    );
                  }
                  return (
                    <button
                      key={`${group.id}-${m.id}`}
                      type="button"
                      data-testid={`model-row-${m.id}`}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted',
                        isActiveRow && 'bg-primary/10 text-primary',
                      )}
                      onClick={() => {
                        chatStore.setProvider(providerForRow, m.id);
                        setOpen(false);
                      }}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="font-medium">{m.name}</span>
                        {m.description && (
                          <span className="text-2xs leading-snug text-muted-foreground">{m.description}</span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {m.contextWindow > 0 ? fmtContext(m.contextWindow) : 'local'}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
